import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import MiniProgramLaunchButton from '../cta/MiniProgramLaunchButton';

const PRIMARY = '#3eb574';
const DARK = '#0f172a';

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const links = [
    { to: '/', label: '首页' },
    { to: '/features', label: '功能' },
    { to: '/pricing', label: '价格' },
    { to: '/cases', label: '案例' },
    { to: '/faq', label: '帮助' },
  ];

  return (
    <>
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: scrolled || open ? 'rgba(255,255,255,0.96)' : 'transparent',
        backdropFilter: scrolled || open ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: scrolled || open ? 'blur(12px)' : 'none',
        borderBottom: scrolled || open ? '1px solid rgba(226,232,240,0.5)' : 'none',
        transition: 'background 0.3s, border 0.3s',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}>
        <div className="px-5 md:px-6" style={{ maxWidth: 1200, margin: '0 auto', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', minWidth: 0 }}>
            <img src="/assets/images/logo.jpg" style={{ width: 32, height: 32, borderRadius: 9, objectFit: 'cover' }} alt="佳达掌柜" />
            <span style={{ fontWeight: 700, fontSize: 16, color: DARK, letterSpacing: '-0.3px', whiteSpace: 'nowrap' }}>佳达掌柜</span>
          </Link>

          <div className="hidden md:flex" style={{ gap: 4, alignItems: 'center' }}>
            {links.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                style={({ isActive }) => ({
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 500,
                  color: isActive ? PRIMARY : '#475569',
                  backgroundColor: isActive ? `${PRIMARY}18` : 'transparent',
                  transition: 'all 0.15s',
                  textDecoration: 'none',
                  display: 'block',
                })}
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden md:flex" style={{ gap: 10, alignItems: 'center' }}>
            <Link to="/pricing" style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: 700,
              borderRadius: 10,
              background: 'white',
              color: DARK,
              border: '2px solid #e2e8f0',
              textDecoration: 'none',
              transition: 'all 0.18s',
            }}>查看价格</Link>
            <MiniProgramLaunchButton
              label="免费体验"
              style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: 700,
              borderRadius: 10,
              background: PRIMARY,
              color: 'white',
              border: '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.18s',
              }}
            />
          </div>

          <button
            onClick={() => setOpen(o => !o)}
            className="flex md:hidden"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 10, margin: -10, flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
            aria-label={open ? '关闭菜单' : '打开菜单'}
            aria-expanded={open}
          >
            <span style={{ width: 22, height: 2, background: DARK, marginBottom: 5, borderRadius: 2, transition: 'transform 0.25s, opacity 0.25s', transform: open ? 'translateY(7px) rotate(45deg)' : 'none' }} />
            <span style={{ width: 22, height: 2, background: DARK, marginBottom: 5, borderRadius: 2, opacity: open ? 0 : 1, transition: 'opacity 0.2s' }} />
            <span style={{ width: 22, height: 2, background: DARK, borderRadius: 2, transition: 'transform 0.25s, opacity 0.25s', transform: open ? 'translateY(-7px) rotate(-45deg)' : 'none' }} />
          </button>
        </div>
      </nav>

      <div
        className="md:hidden"
        aria-hidden={!open}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99,
          background: 'rgba(15,23,42,0.35)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.25s',
        }}
        onClick={() => setOpen(false)}
      />
      <aside
        className="md:hidden"
        aria-hidden={!open}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          width: 'min(82vw, 320px)',
          background: 'white',
          boxShadow: '-12px 0 40px rgba(15,23,42,0.12)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 'calc(60px + env(safe-area-inset-top, 0px))',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
      >
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 16px' }}>
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              style={({ isActive }) => ({
                display: 'block',
                padding: '14px 4px',
                fontSize: 16,
                fontWeight: 600,
                color: isActive ? PRIMARY : '#1e293b',
                borderBottom: '1px solid #f1f5f9',
                textDecoration: 'none',
              })}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div style={{ padding: '12px 20px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Link to="/pricing" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: 700,
            borderRadius: 10,
            background: 'white',
            color: DARK,
            border: '2px solid #e2e8f0',
            textDecoration: 'none',
          }}>查看价格</Link>
          <MiniProgramLaunchButton
            label="免费体验"
            fullWidth
            style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            padding: '13px 20px',
            fontSize: 14,
            fontWeight: 700,
            borderRadius: 10,
            background: PRIMARY,
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            }}
          />
        </div>
      </aside>
    </>
  );
};

export default Navbar;
